// Every accessor here issues the one document. Apollo deduplicates only concurrent
// *identical* queries, so a second flags document would put each consumer back on its own
// request — profile web resolved the flags five times on one page load that way.
//
// The hooks read `cache-and-network`: the cached value renders immediately and each mount
// revalidates, so a flag that flipped — creating or deleting an Indeed Resume can flip
// `enabled` — goes stale only until the next mount, not for the client's lifetime.
// `fetchDualResumesEnabled` stays `network-only` and is the refresh path within one mount.
// A write that can flip a flag must call it before reading back, or the read targets the
// store the write missed.

import type { ApolloClient } from "@apollo/client";

import {
  type DualResumesEnabledQuery,
  DualResumesEnabledDocument,
  useDualResumesEnabledLazyQuery,
  useDualResumesEnabledQuery,
  useDualResumesEnabledSuspenseQuery,
} from "./__generated__/useDualResumesEnabled.gqlgenerated";

import { useCallback } from "react";

/**
 * Keep the two independent in logic. `M1 && !M2` ships, and the bundle payload carries
 * `resume` whenever bundles are requested at all, so an M2-off reader holds those records
 * and must ignore them.
 *
 * The ramp makes M2 a subset of M1, so `!M1 && M2` should not occur — but that is a
 * property of the rollout, enforced nowhere, and collapsing a flag on it breaks silently.
 */
export interface DualResumeFlags {
  /** DualResume M1 — file metadata and signed URLs come from `jobSeekerResumes`. */
  isDualResumesEnabled: boolean;
  /** DualResume M2 — structured resume objects come from `jobSeekerResumes`. */
  isDualResumeObjectsEnabled: boolean;
}

export interface UseDualResumesEnabledOptions {
  skip?: boolean;
}

export interface UseDualResumesEnabledResult extends DualResumeFlags {
  loading: boolean;
}

/**
 * Reports legacy on error. Read both flags even if you only route on one — same request.
 *
 * No `refetch`: re-reading after a write needs fresh flags *and* the read they gate, in
 * that order, which this shape cannot express. Use {@link fetchDualResumesEnabled} there.
 */
export function useDualResumesEnabled(
  options?: UseDualResumesEnabledOptions,
): UseDualResumesEnabledResult {
  const { data, loading, error } = useDualResumesEnabledQuery({
    fetchPolicy: "cache-and-network",
    skip: options?.skip,
  });

  // Only while the value is genuinely unknown. `cache-and-network` reports loading on every
  // revalidation, including the mount where the cached flags are already in hand, and callers
  // gate reads on this — so reporting it there would skip a read that could have run and make
  // its result disappear and come back.
  return { ...toFlags(error ? undefined : data), loading: loading && data === undefined };
}

/**
 * For readers already under a `<Suspense>` boundary, which covers the loading window.
 *
 * The client's global `errorPolicy: "all"` keeps a GraphQL failure non-throwing, so it
 * still degrades to legacy; network failures reach the nearest error boundary.
 */
export function useSuspenseDualResumesEnabled(): DualResumeFlags {
  const { data, error } = useDualResumesEnabledSuspenseQuery({ fetchPolicy: "cache-and-network" });

  return toFlags(error ? undefined : data);
}

/**
 * For readers that need the flags at event time rather than at render: the returned function
 * resolves them when awaited, so a caller acting on a tap cannot read a default while the flags
 * are still in flight.
 *
 * `cache-first`, so a warm cache answers without a request — unlike
 * {@link fetchDualResumesEnabled}, which is the deliberate post-write refresh and always hits
 * the network. Degrades to legacy on failure, as the hooks do.
 */
export function useDualResumesEnabledLazy(): () => Promise<DualResumeFlags> {
  const [fetchFlags] = useDualResumesEnabledLazyQuery({ fetchPolicy: "cache-first" });

  return useCallback(async () => {
    try {
      const { data } = await fetchFlags();
      return toFlags(data);
    } catch {
      return toFlags(undefined);
    }
  }, [fetchFlags]);
}

/**
 * Outside React, for loaders and the post-write refresh. Resolved fresh on every call.
 *
 * `undefined` when the flags can't be read, so a caller can keep its previous value rather
 * than flipping a live cohort to legacy on a transient failure.
 */
export async function fetchDualResumesEnabled(
  // Query-only, so callers holding a narrowed client need no cast.
  client: Pick<ApolloClient<unknown>, "query">,
): Promise<DualResumeFlags | undefined> {
  try {
    const { data } = await client.query<DualResumesEnabledQuery>({
      query: DualResumesEnabledDocument,
      fetchPolicy: "network-only",
    });

    return data?.jobSeekerProfileDualResumesEnabled ? toFlags(data) : undefined;
  } catch {
    return undefined;
  }
}

function toFlags(data: DualResumesEnabledQuery | undefined): DualResumeFlags {
  const payload = data?.jobSeekerProfileDualResumesEnabled;

  return {
    isDualResumesEnabled: payload?.enabled ?? false,
    isDualResumeObjectsEnabled: payload?.allResumesEnabled ?? false,
  };
}
